import { useEffect, useState, useRef } from 'react';
import DeleteIcon from '@material-ui/icons/Delete';
import { useFormContext } from 'react-hook-form';
import _ from 'lodash';
import { t } from '@lingui/macro';
import DataTable from '../../data-table/DataTable';
import { useQueryPostLoader } from '../../data-table/Loaders';
import { useSelectorStyles } from '../../form/ListSelectorStyles';
import { SELECTIONMODE_MULTI, SELECTIONMODE_NONE } from '../../data-table/SelectionContext';
import ListSelectorDialog from '../../form/ListSelectorDialog';
import { COLUMN_TYPES } from '../../data-table/DataTableColumns';
import { ID_FIELD } from '../../form/FormConstants';
import NgpFormButton from '../../form/NgpFormButton';

export default function SiteAdminWanListSelector({
    gridColumns,
    selectedGridColumns,
    dbSelected = [],
    selectedDataSource,
    selectedLoaderFunction,
    availableDataSource,
    availableLoaderFunction,
    availableSelectAll,
    idsDataSource,
    fieldName,
    addButtonText,
    addButtonDisabled = false,
    removeButtonText,
    setSelectionIsDirty,
    isMultiRemove = true,
    selectedEmptyGridText,
    updateSelectionFunction,
}) {
    const classes = useSelectorStyles();
    const { setValue } = useFormContext();
    const selected = useRef([]);
    selected.current = dbSelected;
    const selectedLoaderFn = selectedLoaderFunction || useQueryPostLoader;
    const selectedLoader = selectedLoaderFn(selectedDataSource, selected.current, false);
    const [theSelectedGridColumns, setTheSelectedGridColumns] = useState(selectedGridColumns || gridColumns);
    const [removeDisabled, setRemoveDisabled] = useState(false);
    const [tableSelection, setTableSelection] = useState(undefined);
    const [showDialog, setShowDialog] = useState(false);

    useEffect(() => {
        const columns = _.map(selectedGridColumns || gridColumns(), (theColumn) =>
            _.merge(theColumn, { resizable: false })
        );
        setTheSelectedGridColumns([...deleteColumnDef, ...columns]);
        // Only want to run once
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleTableSelectionChange = (currentTableSelection) => {
        setTableSelection(currentTableSelection);

        setRemoveDisabled(
            !currentTableSelection || (_.isEmpty(currentTableSelection.selection) && !currentTableSelection.inverted)
        );
    };

    const selectionOptions = isMultiRemove
        ? {
              mode: SELECTIONMODE_MULTI,
              allowSelectAll: true,
              idMapping: ID_FIELD,
              onChange: handleTableSelectionChange,
          }
        : { mode: SELECTIONMODE_NONE };

    const updateSelection = (selectedIds) => {
        selected.current = selectedIds;

        if (_.isFunction(updateSelectionFunction)) {
            updateSelectionFunction(selectedIds);
        } else {
            setValue(fieldName, selectedIds);
        }

        if (_.isFunction(setSelectionIsDirty)) {
            setSelectionIsDirty(true);
        }
    };

    const removeSelection = () => {
        updateSelection(
            _.filter(selected.current, (record) => {
                const inSelectionList = _.includes(tableSelection.selection, record);
                return tableSelection.inverted ? inSelectionList : !inSelectionList;
            })
        );
    };

    const removeOne = (record) => {
        updateSelection(_.without(selected.current, record[ID_FIELD]));
    };

    const deleteColumnDef = [
        {
            type: COLUMN_TYPES.ICON,
            title: '',
            width: 45,
            resizable: false,
            icons: [
                {
                    tooltip: t`Delete`,
                    getIcon: (data) => {
                        // This is for the nPoint selector on sites detail, but I think it should work for everything
                        //  For sites, we blend a list of agents that are manually selected as well as agents that
                        //  are part of an IP scope, you can't delete the ones that are part of an IP scope.
                        if (_.indexOf(selected.current, data._id) > -1) return <DeleteIcon />;
                    },
                    clickHandler: removeOne,
                },
            ],
        },
    ];

    return (
        <>
            <div className={classes.selectorContainer}>
                <div className={classes.selectorActions}>
                    <NgpFormButton onClick={() => setShowDialog(true)} disabled={addButtonDisabled}>
                        {addButtonText}
                    </NgpFormButton>
                    {isMultiRemove && (
                        <NgpFormButton onClick={removeSelection} disabled={removeDisabled}>
                            {removeButtonText}
                        </NgpFormButton>
                    )}
                </div>
                <DataTable
                    loader={selectedLoader}
                    columns={theSelectedGridColumns}
                    showSelectionColumn={isMultiRemove}
                    disableCSVExport
                    disableColumnRemover
                    sorters={selectedDataSource.initialSort}
                    selectionOptions={selectionOptions}
                    emptyText={selectedEmptyGridText}
                />
            </div>

            <ListSelectorDialog
                gridColumns={gridColumns}
                dbSelected={dbSelected}
                availableDataSource={availableDataSource}
                availableLoaderFunction={availableLoaderFunction}
                availableSelectAll={availableSelectAll}
                idsDataSource={idsDataSource}
                addButtonText={addButtonText}
                showDialog={showDialog}
                setShowDialog={setShowDialog}
                selected={selected.current}
                updateSelection={updateSelection}
            />
        </>
    );
}
